import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardBody, PageContainer } from "../components/Card";
import { PageHeader } from "../components/PageHeader";
import { Button, IconButton } from "../components/Button";
import { Modal, ModalBody } from "../components/Modal";
import { ConfirmModal } from "../components/ConfirmModal";
import { Field, Input, Select, Textarea } from "../components/Form";
import { Badge } from "../components/Badge";
import { Banner, EmptyState, ListStates } from "../components/States";
import { Icon } from "../components/Icon";
import { Toolbar } from "../components/Toolbar";
import { useAsync, useAction } from "../general/useAsync";
import { useToast } from "../general/Toast";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { AssessmentGroupModel, AssessmentModel } from "../api/APIClient";
import * as Configuration from "../configuration/ConfigurationStore";
import { ModulePicker } from "./ModulePicker";
import * as Store from "./ActivitiesStore";

/**
 * The assessment tree for one qualification module.
 *
 * A tree of `AssessmentGroup` nodes ("Term 1" > "Division Tests"), with `Assessment` leaves that
 * carry a mark total. Every group has a group *type*, and that type is what procedure selectors match
 * on - so the tree's shape and its type labels are what make a procedure portable across modules.
 * Renaming a group is cosmetic; changing its type changes which procedures find it.
 */

interface TreeNode {
    group: AssessmentGroupModel;
    children: TreeNode[];
    assessments: AssessmentModel[];
}

function buildTree(groups: AssessmentGroupModel[], assessments: AssessmentModel[]): { roots: TreeNode[]; orphanAssessments: AssessmentModel[] } {
    const byId = new Map<string, TreeNode>();
    groups.forEach((g) => byId.set(g.id ?? "", { group: g, children: [], assessments: [] }));

    const roots: TreeNode[] = [];
    byId.forEach((node) => {
        const parentID = node.group.parentGroupID;
        const parent = parentID ? byId.get(parentID) : undefined;
        if (parent) parent.children.push(node);
        else roots.push(node);
    });

    const orphanAssessments: AssessmentModel[] = [];
    assessments.forEach((a) => {
        const owner = a.groupID ? byId.get(a.groupID) : undefined;
        if (owner) owner.assessments.push(a);
        else orphanAssessments.push(a);
    });

    const sortNode = (n: TreeNode) => {
        n.children.sort((a, b) => (a.group.name ?? "").localeCompare(b.group.name ?? ""));
        n.assessments.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
        n.children.forEach(sortNode);
    };
    roots.sort((a, b) => (a.group.name ?? "").localeCompare(b.group.name ?? ""));
    roots.forEach(sortNode);

    return { roots, orphanAssessments };
}

type GroupEditState = { mode: "create"; parentGroupID?: string } | { mode: "edit"; group: AssessmentGroupModel } | null;
type AssessmentEditState = { mode: "create"; groupID: string } | { mode: "edit"; assessment: AssessmentModel } | null;

export const StructureEditor: React.FC = () => {
    const toast = useToast();
    const canWrite = usePermission(Permissions.ACTIVITIES_WRITE);

    const [searchParams, setSearchParams] = useSearchParams();
    const moduleID = searchParams.get("module") ?? "";
    const setModuleID = (id: string) => {
        setSearchParams(id ? { module: id } : {});
    };
    const [groupEdit, setGroupEdit] = React.useState<GroupEditState>(null);
    const [assessmentEdit, setAssessmentEdit] = React.useState<AssessmentEditState>(null);
    const [deletingGroup, setDeletingGroup] = React.useState<AssessmentGroupModel | null>(null);
    const [deletingAssessment, setDeletingAssessment] = React.useState<AssessmentModel | null>(null);
    const [moving, setMoving] = React.useState<AssessmentGroupModel | null>(null);

    const structure = useAsync(() => (moduleID ? Store.getModuleStructure(moduleID) : Promise.resolve(undefined)), [moduleID], {
        enabled: !!moduleID,
    });

    const groupTypes = useAsync(() => Configuration.listGroupTypes(), [], { initial: [] });
    const assessmentTypes = useAsync(() => Configuration.listAssessmentTypes(), [], { initial: [] });

    const groups = structure.data?.groups ?? [];
    const assessments = structure.data?.assessments ?? [];
    const { roots, orphanAssessments } = React.useMemo(() => buildTree(groups, assessments), [groups, assessments]);

    const removeGroup = useAction(async () => {
        if (!deletingGroup) return;
        await Store.deleteGroup(deletingGroup.id ?? "");
        toast.success("Group deleted.");
        setDeletingGroup(null);
        structure.refresh();
    });

    const removeAssessment = useAction(async () => {
        if (!deletingAssessment) return;
        await Store.deleteAssessment(deletingAssessment.id ?? "");
        toast.success("Assessment deleted.");
        setDeletingAssessment(null);
        structure.refresh();
    });

    return (
        <PageContainer>
            <PageHeader
                title="Assessment structure"
                icon={["fad", "sitemap"]}
                description="The group tree and assessments inside one qualification module. Procedure selectors match on group type, not on individual groups."
                actions={
                    canWrite &&
                    moduleID && (
                        <Button icon={["fad", "plus"]} onClick={() => setGroupEdit({ mode: "create" })}>
                            Add top-level group
                        </Button>
                    )
                }
            />

            <Toolbar>
                <ModulePicker value={moduleID} onChange={setModuleID} />
            </Toolbar>

            {!moduleID && (
                <Card>
                    <EmptyState
                        icon={["fad", "sitemap"]}
                        title="Pick a qualification module"
                        hint="An assessment tree belongs to one subject, for one cohort, in one cycle."
                    />
                </Card>
            )}

            {moduleID && (
                <Card>
                    <ListStates
                        isLoading={structure.isLoading}
                        error={structure.error}
                        onRetry={structure.refresh}
                        isEmpty={roots.length === 0 && orphanAssessments.length === 0}
                        empty={{
                            icon: ["fad", "folder-tree"],
                            title: "This module has no assessment structure yet",
                            hint: canWrite
                                ? "Start with a top-level group - typically a term or a division - then add assessments inside it."
                                : undefined,
                        }}
                    >
                        <CardBody>
                            {orphanAssessments.length > 0 && (
                                <Banner tone="warning" className="mb-4">
                                    {orphanAssessments.length} assessment(s) are not inside any group in this module. They will not be
                                    found by a group-path selector.
                                </Banner>
                            )}

                            <div className="space-y-1">
                                {roots.map((node) => (
                                    <GroupNode
                                        key={node.group.id}
                                        node={node}
                                        depth={0}
                                        canWrite={canWrite}
                                        onAddGroup={(parentGroupID) => setGroupEdit({ mode: "create", parentGroupID })}
                                        onEditGroup={(group) => setGroupEdit({ mode: "edit", group })}
                                        onMoveGroup={setMoving}
                                        onDeleteGroup={setDeletingGroup}
                                        onAddAssessment={(groupID) => setAssessmentEdit({ mode: "create", groupID })}
                                        onEditAssessment={(assessment) => setAssessmentEdit({ mode: "edit", assessment })}
                                        onDeleteAssessment={setDeletingAssessment}
                                    />
                                ))}
                            </div>
                        </CardBody>
                    </ListStates>
                </Card>
            )}

            <GroupFormModal
                state={groupEdit}
                moduleID={moduleID}
                groupTypes={groupTypes.data ?? []}
                onClose={() => setGroupEdit(null)}
                onSaved={() => {
                    setGroupEdit(null);
                    structure.refresh();
                }}
            />

            <AssessmentFormModal
                state={assessmentEdit}
                assessmentTypes={assessmentTypes.data ?? []}
                onClose={() => setAssessmentEdit(null)}
                onSaved={() => {
                    setAssessmentEdit(null);
                    structure.refresh();
                }}
            />

            <MoveGroupModal
                group={moving}
                groups={groups}
                onClose={() => setMoving(null)}
                onMoved={() => {
                    setMoving(null);
                    structure.refresh();
                }}
            />

            <ConfirmModal
                isOpen={deletingGroup !== null}
                onClose={() => setDeletingGroup(null)}
                onConfirm={removeGroup.run}
                title="Delete group"
                icon={["fad", "trash"]}
                busy={removeGroup.busy}
                error={removeGroup.error}
                confirmLabel="Delete"
                body={
                    <>
                        Delete <strong>{deletingGroup?.name}</strong>? Anything inside it goes too, and any procedure that selected this
                        part of the tree will stop finding it. This cannot be undone.
                    </>
                }
            />

            <ConfirmModal
                isOpen={deletingAssessment !== null}
                onClose={() => setDeletingAssessment(null)}
                onConfirm={removeAssessment.run}
                title="Delete assessment"
                icon={["fad", "trash"]}
                busy={removeAssessment.busy}
                error={removeAssessment.error}
                confirmLabel="Delete"
                body={
                    <>
                        Delete <strong>{deletingAssessment?.name}</strong>? Marks captured against it are removed with it. This cannot be
                        undone.
                    </>
                }
            />
        </PageContainer>
    );
};

const GroupNode: React.FC<{
    node: TreeNode;
    depth: number;
    canWrite: boolean;
    onAddGroup: (parentGroupID: string) => void;
    onEditGroup: (group: AssessmentGroupModel) => void;
    onMoveGroup: (group: AssessmentGroupModel) => void;
    onDeleteGroup: (group: AssessmentGroupModel) => void;
    onAddAssessment: (groupID: string) => void;
    onEditAssessment: (assessment: AssessmentModel) => void;
    onDeleteAssessment: (assessment: AssessmentModel) => void;
}> = ({ node, depth, canWrite, onAddGroup, onEditGroup, onMoveGroup, onDeleteGroup, onAddAssessment, onEditAssessment, onDeleteAssessment }) => {
    const [open, setOpen] = React.useState(true);
    const { group, children, assessments } = node;
    const hasContents = children.length > 0 || assessments.length > 0;

    return (
        <div>
            <div
                className="flex items-center gap-2 rounded-md px-2 py-2 transition-colors hover:bg-surface-hover"
                style={{ paddingLeft: `${depth * 1.5 + 0.5}rem` }}
            >
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className={`text-xs text-text-weak ${hasContents ? "" : "invisible"}`}
                    aria-label={open ? "Collapse" : "Expand"}
                >
                    <Icon icon={["fad", open ? "chevron-down" : "chevron-right"]} />
                </button>

                <Icon icon={["fad", "folder"]} className="text-primary" />
                <span className="font-medium text-text-strong">{group.name}</span>

                {group.assessmentGroupTypeName ? (
                    <Badge tone="primary" title="Procedure selectors match on this type, not on the group itself.">
                        {group.assessmentGroupTypeName}
                    </Badge>
                ) : (
                    <Badge tone="danger" title="A group with no type cannot be found by a selector.">
                        No type
                    </Badge>
                )}

                {group.description && <span className="truncate text-xs text-text-weak">{group.description}</span>}

                {canWrite && (
                    <div className="ml-auto flex items-center gap-1">
                        <IconButton icon={["fad", "folder-plus"]} label="Add subgroup" onClick={() => onAddGroup(group.id ?? "")} />
                        <IconButton icon={["fad", "file-circle-plus"]} label="Add assessment" onClick={() => onAddAssessment(group.id ?? "")} />
                        <IconButton icon={["fad", "pen-to-square"]} label="Edit group" onClick={() => onEditGroup(group)} />
                        <IconButton icon={["fad", "arrows-up-down-left-right"]} label="Move group" onClick={() => onMoveGroup(group)} />
                        <IconButton icon={["fad", "trash"]} variant="outline-danger" label="Delete group" onClick={() => onDeleteGroup(group)} />
                    </div>
                )}
            </div>

            {open && (
                <>
                    {assessments.map((assessment) => (
                        <div
                            key={assessment.id}
                            className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-surface-hover"
                            style={{ paddingLeft: `${(depth + 1) * 1.5 + 1.5}rem` }}
                        >
                            <Icon icon={["fad", "file-pen"]} className="text-text-weak" />
                            <span className="text-text">{assessment.name}</span>
                            {assessment.assessmentTypeName && <Badge tone="neutral">{assessment.assessmentTypeName}</Badge>}
                            <span className="text-xs text-text-weak">out of {assessment.total}</span>

                            {canWrite && (
                                <div className="ml-auto flex items-center gap-1">
                                    <IconButton icon={["fad", "pen-to-square"]} label="Edit assessment" onClick={() => onEditAssessment(assessment)} />
                                    <IconButton
                                        icon={["fad", "trash"]}
                                        variant="outline-danger"
                                        label="Delete assessment"
                                        onClick={() => onDeleteAssessment(assessment)}
                                    />
                                </div>
                            )}
                        </div>
                    ))}

                    {children.map((child) => (
                        <GroupNode
                            key={child.group.id}
                            node={child}
                            depth={depth + 1}
                            canWrite={canWrite}
                            onAddGroup={onAddGroup}
                            onEditGroup={onEditGroup}
                            onMoveGroup={onMoveGroup}
                            onDeleteGroup={onDeleteGroup}
                            onAddAssessment={onAddAssessment}
                            onEditAssessment={onEditAssessment}
                            onDeleteAssessment={onDeleteAssessment}
                        />
                    ))}
                </>
            )}
        </div>
    );
};

const GroupFormModal: React.FC<{
    state: GroupEditState;
    moduleID: string;
    groupTypes: Configuration.AssessmentTypeModel[];
    onClose: () => void;
    onSaved: () => void;
}> = ({ state, moduleID, groupTypes, onClose, onSaved }) => {
    const toast = useToast();
    const isOpen = state !== null;
    const isEdit = state?.mode === "edit";

    const [name, setName] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [typeID, setTypeID] = React.useState("");

    React.useEffect(() => {
        if (!isOpen) return;
        const group = state?.mode === "edit" ? state.group : undefined;
        setName(group?.name ?? "");
        setDescription(group?.description ?? "");
        setTypeID(group?.assessmentGroupTypeID ?? "");
    }, [isOpen, state]);

    const save = useAction(async () => {
        if (state?.mode === "edit") {
            await Store.updateGroup(state.group.id ?? "", { name, description, assessmentGroupTypeID: typeID });
            toast.success("Group updated.");
        } else {
            await Store.createGroup({
                name,
                description,
                assessmentGroupTypeID: typeID,
                parentGroupID: state?.mode === "create" ? state.parentGroupID : undefined,
                qualificationModuleID: moduleID,
            });
            toast.success("Group created.");
        }
        onSaved();
    });

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? "Edit group" : "Add group"}
            icon={["fad", "folder"]}
            footer={
                <>
                    <Button variant="outline-secondary" onClick={onClose} disabled={save.busy}>
                        Cancel
                    </Button>
                    <Button onClick={save.run} loading={save.busy} disabled={!name.trim() || !typeID}>
                        {isEdit ? "Save changes" : "Create"}
                    </Button>
                </>
            }
        >
            <ModalBody>
                <Field label="Name" required>
                    <Input value={name} placeholder="Term 1" onChange={(e) => setName(e.target.value)} />
                </Field>
                <Field
                    label="Group type"
                    required
                    hint="Procedure selectors match on the type. A group with no type is invisible to them."
                >
                    <Select value={typeID} placeholder="Select a type" onChange={(e) => setTypeID(e.target.value)}>
                        {groupTypes.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.name}
                            </option>
                        ))}
                    </Select>
                </Field>
                <Field label="Description">
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
                </Field>

                {save.error && <Banner tone="danger">{save.error}</Banner>}
            </ModalBody>
        </Modal>
    );
};

const AssessmentFormModal: React.FC<{
    state: AssessmentEditState;
    assessmentTypes: Configuration.AssessmentTypeModel[];
    onClose: () => void;
    onSaved: () => void;
}> = ({ state, assessmentTypes, onClose, onSaved }) => {
    const toast = useToast();
    const isOpen = state !== null;
    const isEdit = state?.mode === "edit";

    const [name, setName] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [typeID, setTypeID] = React.useState("");
    const [total, setTotal] = React.useState("100");

    React.useEffect(() => {
        if (!isOpen) return;
        const assessment = state?.mode === "edit" ? state.assessment : undefined;
        setName(assessment?.name ?? "");
        setDescription(assessment?.description ?? "");
        setTypeID(assessment?.assessmentTypeID ?? "");
        setTotal(assessment?.total === undefined ? "100" : String(assessment.total));
    }, [isOpen, state]);

    const save = useAction(async () => {
        const model = {
            name,
            description,
            assessmentTypeID: typeID,
            total: Number(total),
            groupID: state?.mode === "edit" ? state.assessment.groupID : state?.groupID,
        };

        if (state?.mode === "edit") {
            await Store.updateAssessment(state.assessment.id ?? "", model);
            toast.success("Assessment updated.");
        } else {
            await Store.createAssessment(model);
            toast.success("Assessment created.");
        }
        onSaved();
    });

    const totalValue = Number(total);
    const totalInvalid = !total.trim() || Number.isNaN(totalValue) || totalValue <= 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? "Edit assessment" : "Add assessment"}
            icon={["fad", "file-pen"]}
            footer={
                <>
                    <Button variant="outline-secondary" onClick={onClose} disabled={save.busy}>
                        Cancel
                    </Button>
                    <Button onClick={save.run} loading={save.busy} disabled={!name.trim() || !typeID || totalInvalid}>
                        {isEdit ? "Save changes" : "Create"}
                    </Button>
                </>
            }
        >
            <ModalBody>
                <Field label="Name" required>
                    <Input value={name} placeholder="Test 1" onChange={(e) => setName(e.target.value)} />
                </Field>
                <Field label="Assessment type" required>
                    <Select value={typeID} placeholder="Select a type" onChange={(e) => setTypeID(e.target.value)}>
                        {assessmentTypes.map((t) => (
                            <option key={t.id} value={t.id}>
                                {t.name}
                            </option>
                        ))}
                    </Select>
                </Field>
                <Field
                    label="Total"
                    required
                    hint="Marks out of. Must be greater than zero - the database enforces it. Mark entry against this assessment derives its total from here."
                >
                    <Input
                        type="number"
                        value={total}
                        onChange={(e) => setTotal(e.target.value)}
                        error={totalInvalid && total.trim() ? "Total must be a number greater than zero." : undefined}
                    />
                </Field>
                <Field label="Description">
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
                </Field>

                {save.error && <Banner tone="danger">{save.error}</Banner>}
            </ModalBody>
        </Modal>
    );
};

const MoveGroupModal: React.FC<{
    group: AssessmentGroupModel | null;
    groups: AssessmentGroupModel[];
    onClose: () => void;
    onMoved: () => void;
}> = ({ group, groups, onClose, onMoved }) => {
    const toast = useToast();
    const [parentID, setParentID] = React.useState("");

    React.useEffect(() => {
        if (group) setParentID(group.parentGroupID ?? "");
    }, [group]);

    /**
     * A group cannot become its own descendant's child. The server would reject the cycle, but
     * offering the option at all invites the mistake, so the candidates are filtered here.
     */
    const candidates = React.useMemo(() => {
        if (!group) return [];
        const descendants = new Set<string>();
        const collect = (id: string) => {
            descendants.add(id);
            groups.filter((g) => g.parentGroupID === id).forEach((g) => collect(g.id ?? ""));
        };
        collect(group.id ?? "");
        return groups.filter((g) => !descendants.has(g.id ?? ""));
    }, [group, groups]);

    const move = useAction(async () => {
        if (!group) return;
        await Store.moveGroup(group.id ?? "", parentID || undefined);
        toast.success("Group moved.");
        onMoved();
    });

    return (
        <Modal
            isOpen={group !== null}
            onClose={onClose}
            title={`Move ${group?.name ?? "group"}`}
            icon={["fad", "arrows-up-down-left-right"]}
            footer={
                <>
                    <Button variant="outline-secondary" onClick={onClose} disabled={move.busy}>
                        Cancel
                    </Button>
                    <Button onClick={move.run} loading={move.busy}>
                        Move
                    </Button>
                </>
            }
        >
            <ModalBody>
                <Field
                    label="New parent"
                    hint="Leave empty to move the group to the top level of its module. A group cannot be moved inside itself."
                >
                    <Select value={parentID} placeholder="Top level" onChange={(e) => setParentID(e.target.value)}>
                        {candidates.map((g) => (
                            <option key={g.id} value={g.id}>
                                {g.name} {g.assessmentGroupTypeName ? `(${g.assessmentGroupTypeName})` : ""}
                            </option>
                        ))}
                    </Select>
                </Field>

                <Banner tone="warning">
                    Moving a group changes the path a selector has to walk to reach it. Any procedure assignment that targeted it should be
                    regenerated afterwards.
                </Banner>

                {move.error && <Banner tone="danger" className="mt-3">{move.error}</Banner>}
            </ModalBody>
        </Modal>
    );
};

export default StructureEditor;
